import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';

import { FaAngleLeft } from 'react-icons/fa6';
import { FaInfoCircle } from 'react-icons/fa';

import CardsProgressSteps from '../../../../components/ProgressStep/CardsProgressSteps';
import TransactionPinModal from '../../../../components/TransactionPinModal'; // adjust path if needed

import './sendMoney.css'
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { bankMoneyTransfer, transferFunds } from '../../../../actions/payments';
import { refreshUserDetails } from '../../../../actions/user';
import { formatAmount, getCurrencyInfoByCurrencyCode as getCurrencyInfo, dataURLtoFile } from '../../../../helpers/currency';
import { handleValidationError } from '../../../../actions/validation';

const Step3SendMoney = () => {
  const userDetails = useSelector((state) => state.user.user);
  const hasSetPin = userDetails?.has_set_pin || false;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showFeeDetails, setShowFeeDetails] = useState(false);
  const { currencyOptions } = useSelector((state) => state.country);
  
  const sendMoneyDetails = location.state?.sendMoneyDetails;
  const txnAmountDetails = location.state?.txnAmountDetails;
  const sendMoneyOverview = location.state?.sendMoneyOverview;
  const localTransferType = sendMoneyDetails?.payload?.location?.slug === "local" ? true : false;

  const getCurrencyInfoByCurrencyCode = (code) => {
    return getCurrencyInfo(currencyOptions, code);
  };

  const getRecipientName = () => {
    if (['bank', 'mobile_money'].includes(sendMoneyDetails?.payload?.transfer_type)) {
      return sendMoneyOverview?.recipientBankAccount?.account_name || 'Recipient';
    } else if (sendMoneyDetails?.payload?.transfer_type === 'cardplot') {
      return sendMoneyDetails?.payload?.recipient?.username || 'Recipient';
    }
    return 'Recipient';
  };

  const recipientName = getRecipientName();

  const getInitials = (name) => {
    if (!name || name === 'Not Stated') return 'R';
    const names = name.trim().split(' ');
    let initials = names[0].substring(0, 1).toUpperCase();
    if (names.length > 1) {
      initials += names[names.length - 1].substring(0, 1).toUpperCase();
    }
    return initials;
  };
  
  useEffect(() => {
    if (!sendMoneyDetails) {
      navigate('/user/send_money/step2', { replace: true });
    }
  }, [sendMoneyDetails, navigate]);

  const handleGoBack = () => {
    const converterSendingDetails = {
      ...txnAmountDetails,
      sendCurrency: sendMoneyDetails?.sendCurrency,
      receiveCurrency: sendMoneyDetails?.receiveCurrency,
      sendAmount: sendMoneyDetails?.sendAmount,
      receiveAmount: sendMoneyDetails?.receiveAmount,
      exchangeRate: sendMoneyDetails?.exchangeRate,
    };
    navigate("/user/send_money/step2", { state: { payload: sendMoneyDetails.payload, converterSendingDetails } }); 
  }    

  // Now receives the PIN directly from TransactionPinModal
  const handleConfirmPayment = async (pin) => {
    if (!pin || pin.length !== 4) {
      toast.error("Kindly enter your PIN");
      return;
    }

    let credentials;
    const transferTypeIsMomoOrBank = sendMoneyDetails.payload?.transfer_type != 'cardplot';

    const isChinaTransfer = sendMoneyDetails.payload?.recipient?.recipientCountry === 'CN' || sendMoneyDetails.payload?.currency_to_send;
    const destinationCurrency = isChinaTransfer ? 'CNY' : sendMoneyDetails?.receiveCurrency;
    const currencyToSend = sendMoneyDetails.payload?.currency_to_send;
    const invoiceFile = sendMoneyDetails.payload?.invoice_document_file || dataURLtoFile(sendMoneyDetails.payload?.invoice_document || sendMoneyDetails.payload?.transferDocument, sendMoneyDetails.payload?.invoiceDocumentName || sendMoneyDetails.payload?.transferDocumentName);

    if (sendMoneyDetails.payload?.transfer_type === 'cardplot') {
      credentials = {
        amount: sendMoneyDetails?.receiveAmount,
        from_amount: sendMoneyDetails?.sendAmount,
        recipient_id: sendMoneyDetails?.payload?.recipient?.recipient_id,
        transfer_type: sendMoneyDetails?.payload?.transfer_type,
        wallet_id: sendMoneyDetails?.payload?.fundingSource?.id,
        currency: destinationCurrency,
        ...(currencyToSend ? { currency_to_send: currencyToSend } : {}),
        ...(sendMoneyDetails?.payload?.transactionDescription ? { description: sendMoneyDetails?.payload?.transactionDescription } : {}),
        pin,
      };    
    } else if (transferTypeIsMomoOrBank) {
      credentials = {
        wallet_id: sendMoneyDetails?.payload?.fundingSource?.id,
        amount: sendMoneyDetails?.receiveAmount,
        transfer_type: sendMoneyDetails?.payload?.transfer_type,
        currency: destinationCurrency,
        ...(currencyToSend ? { currency_to_send: currencyToSend } : {}),
        bank_details: {
          bank_id: sendMoneyDetails?.payload?.recipient?.bank_uuid,
          account_number: sendMoneyDetails?.payload?.recipient?.accountNumber,
          account_name: sendMoneyDetails?.payload?.recipient?.username,
        },
        ...(sendMoneyDetails?.payload?.transactionDescription ? { 
          description: sendMoneyDetails?.payload?.transactionDescription,
          narration: sendMoneyDetails?.payload?.transactionDescription,
        } : {}),
        ...(sendMoneyDetails?.payload?.transferPurpose ? { purpose: sendMoneyDetails?.payload?.transferPurpose.value } : {}),
        ...(sendMoneyDetails?.payload?.transferRecipientType ? { recipient_type: sendMoneyDetails?.payload?.transferRecipientType.value } : {}),
        ...(sendMoneyDetails?.payload?.transferRecipientRelationship ? { recipient_relationship: sendMoneyDetails?.payload?.transferRecipientRelationship.value } : {}),
        pin,
      };
    } else {
      toast.error("Invalid credentials.");
      return;
    }

    let finalPayload = {...credentials, session_id: sendMoneyOverview?.sessionId};
    if (invoiceFile) {
      const formData = new FormData();
      Object.keys(credentials).forEach((key) => {
        if (key === 'bank_details' && typeof credentials[key] === 'object' && credentials[key] !== null) {
          Object.keys(credentials.bank_details).forEach((subKey) => {
            if (credentials.bank_details[subKey] !== undefined && credentials.bank_details[subKey] !== null) {
              formData.append(`bank_details[${subKey}]`, credentials.bank_details[subKey]);
            }
          });
        } else if (credentials[key] !== undefined && credentials[key] !== null) {
          formData.append(key, credentials[key]);
        }
      });
      formData.append('invoice_document', invoiceFile);
      formData.append('invoice_document', invoiceFile);
      formData.append('session_id', sendMoneyOverview?.sessionId);
      finalPayload = formData;
    }

    setIsLoading(true);
  
    try {
      let response;
      let totalDebitAmount;
      if (sendMoneyDetails.payload?.transfer_type === 'cardplot') {
        response = await transferFunds(finalPayload);
        totalDebitAmount = 
          response?.data?.transaction?.amount || 
          response?.data?.amount || 
          response?.transaction?.amount || 
          sendMoneyDetails?.sendAmount;
        toast.success("Payment Successful!");
      } else if (transferTypeIsMomoOrBank) {
        response = await bankMoneyTransfer(finalPayload);
        totalDebitAmount = 
          response?.data?.transfer?.amount || 
          response?.data?.amount || 
          response?.transfer?.amount || 
          response?.amount || 
          sendMoneyOverview?.totalAmount || 
          sendMoneyDetails?.totalAmount || 
          sendMoneyDetails?.sendAmount;
      }

      try {
        await refreshUserDetails(dispatch);
      } catch (userErr) {
        console.warn("Failed to refresh user details:", userErr);
      }

      const currentState = {
        recipientName: sendMoneyDetails?.payload?.recipient?.username || recipientName,
        amount: sendMoneyDetails?.sendAmount,
        sendCurrency: sendMoneyDetails?.sendCurrency,
        totalAmount: totalDebitAmount || sendMoneyOverview?.totalAmount || sendMoneyDetails?.totalAmount || sendMoneyDetails?.sendAmount,
        status: sendMoneyDetails.payload?.transfer_type === 'cardplot' ? 'successful' : 'pending',
        transaction: response?.data?.transaction || response?.data?.transfer || response?.data || response?.transfer || response,
        sendMoneyOverview: sendMoneyOverview
      };
      navigate('/user/send_money/success', { state: currentState });
    } catch (error) {
      handleValidationError(error, toast, "Verification failed, Please try again");
    } finally {
      setIsLoading(false);
    }
  };

  const calculateRemainingBalance = (totalWalletBal, sendingAmount) => {
    const remainingBal = parseFloat(totalWalletBal.replace(/,/g, '')) - parseFloat(sendingAmount);
    return formatAmount(remainingBal);
  };

  return (
    <section className='create__card__page send__money__page' style={{ paddingTop: "30px" }}>
      <div className="container">
        <div className="d-flex align-items-center mb-2">
          <button 
            onClick={handleGoBack} 
            className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm back-button-hover" 
            style={{ backgroundColor: '#fff', color: '#4a4a4a', border: '1px solid #eaeaea', transition: 'all 0.2s ease', fontWeight: '500' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <FaAngleLeft size={14} />
            <span className="small">Go back</span>
          </button>
        </div>
      </div>
      <section className="mt-3">
        <CardsProgressSteps currentStep={3} stepLabels={["Recipient", "Amount", "Review", "Success"]} />
      </section>

      <div className="container mt-4">
        <div className="send__money__card send__money__form" style={{ padding: '30px 20px', borderRadius: '20px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          {/* Top Section */}
          <div className="text-center mb-4">
            <div className="d-flex justify-content-center mb-3">
              <div className="rounded-circle d-flex align-items-center justify-content-center position-relative" 
                  style={{ width: '64px', height: '64px', border: '1px solid #eaeaea', backgroundColor: '#fff', fontSize: '24px', fontWeight: '600', color: '#333' }}>
                {getInitials(recipientName)}
                <div className="position-absolute d-flex align-items-center justify-content-center" 
                    style={{ bottom: '-2px', right: '-2px', width: '24px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '50%', border: '2px solid #fff', fontSize: '10px', fontWeight: 'bold' }}>
                  {sendMoneyOverview?.receiveCurrency?.slice(0, 2)}
                </div>
              </div>
            </div>
            <p className="text-muted mb-1" style={{ fontSize: '16px', fontWeight: '500', color: '#555' }}>{recipientName} gets exactly</p>
            <h1 style={{ fontSize: '42px', fontWeight: '800', margin: '0', letterSpacing: '-1px', color: '#111' }}>
              {formatAmount(sendMoneyOverview?.receiveAmount)}
            </h1>
            <p className="text-muted mt-1" style={{ fontSize: '15px' }}>
              {getCurrencyInfoByCurrencyCode(sendMoneyOverview?.receiveCurrency)?.name || getCurrencyInfoByCurrencyCode(sendMoneyOverview?.receiveCurrency)?.currencyName || sendMoneyOverview?.receiveCurrency}
            </p>
          </div>

          <div className="px-2 px-md-4 mt-3">
            <hr style={{ borderTop: '1px dashed #2f2f2f', margin: '0' }} />
            {/* Sending to */}
            <div className="d-flex justify-content-between align-items-center py-3">
              <span style={{ color: '#666', fontSize: '15px' }}>Sending to</span>
              <span style={{ color: '#2b5043', fontSize: '15px', fontWeight: '600' }} className="d-flex align-items-center">
                {recipientName}  
              </span>
            </div>
            
            {['bank', 'mobile_money'].includes(sendMoneyDetails?.payload?.transfer_type) && (
              <div className="d-flex justify-content-between align-items-center pb-3 mt-[-10px]">
                <span style={{ color: '#666', fontSize: '15px' }}>Bank Details</span>
                <span style={{textAlign: 'right' }}>
                  <span style={{ color: '#2b5043', fontSize: '15px'}}>
                    {sendMoneyOverview?.recipientBankAccount?.account_number}
                  </span>
                  <br/>
                  <span style={{ color: '#888', fontSize: '13px'}}>
                    {sendMoneyOverview?.recipientBankAccount?.bank_name} 
                  </span>
                </span>
              </div>
            )}

            <hr style={{ borderTop: '1px dashed #2f2f2f', margin: '0' }} />

            {/* You pay */}
            <div className="d-flex justify-content-between align-items-center py-3">
              <span style={{ color: '#666', fontSize: '15px' }}>You pay</span>
              <span style={{ color: '#2b5043', fontSize: '15px', fontWeight: '600' }} className="d-flex align-items-center">
                {getCurrencyInfoByCurrencyCode(sendMoneyOverview?.sendCurrency).symbol}{formatAmount(sendMoneyOverview?.totalAmount)}
              </span>
            </div>

            {/* Total fees */}
            <div className="d-flex justify-content-between align-items-center pb-3">
              <span style={{ color: '#666', fontSize: '15px' }} className="d-flex align-items-center">
                Total fees (included)
                {sendMoneyOverview?.appliedFees?.length > 0 && (
                  <FaInfoCircle 
                    className="ms-2" 
                    color="#aaa"
                    onClick={() => setShowFeeDetails(!showFeeDetails)}
                    style={{ cursor: 'pointer', fontSize: '0.9em' }}
                  />
                )}
              </span>
              <span style={{ color: '#333', fontSize: '15px', fontWeight: '500' }}>
                {getCurrencyInfoByCurrencyCode(sendMoneyOverview?.sendCurrency).symbol}{
                  formatAmount((typeof sendMoneyOverview?.fee === 'object' && sendMoneyOverview?.fee !== null 
                    ? sendMoneyOverview.fee.total_amount 
                    : sendMoneyOverview?.fee) / (sendMoneyOverview?.rawExchangeRate || 1))
                }
              </span>
            </div>
            
            {/* Expanded Fees */}
            {showFeeDetails && sendMoneyOverview?.appliedFees && sendMoneyOverview?.appliedFees.map((fee, index) => 
              <div key={index} className="d-flex justify-content-between align-items-center pb-3" style={{ paddingLeft: '1rem', opacity: 0.8 }}>
                <span style={{ color: '#888', fontSize: '14px' }}>{fee.label}</span>
                <span style={{ color: '#888', fontSize: '14px' }}>
                  {getCurrencyInfoByCurrencyCode(sendMoneyOverview.sendCurrency).symbol}{formatAmount(fee.amount / (sendMoneyOverview?.rawExchangeRate || 1))}
                </span>
              </div>
            )}
            {sendMoneyOverview?.appliedStatutoryFees && sendMoneyOverview?.appliedStatutoryFees.map(statutoryFee => 
              <div key={statutoryFee.id} className="d-flex justify-content-between align-items-center pb-3" style={{ paddingLeft: '1rem', opacity: 0.8 }}>
                <span style={{ color: '#888', fontSize: '14px' }}>{statutoryFee.label}</span>
                <span style={{ color: '#888', fontSize: '14px' }}>
                  {getCurrencyInfoByCurrencyCode(sendMoneyOverview.sendCurrency).symbol}{formatAmount((statutoryFee.total_amount || statutoryFee.amount) / (sendMoneyOverview?.rawExchangeRate || 1))}
                </span>
              </div>
            )}

            {/* Amount we'll convert */}
            {!localTransferType && (
              <div className="d-flex justify-content-between align-items-center pb-3">
                <span style={{ color: '#666', fontSize: '15px' }}>Total amount we'll convert</span>
                <span style={{ color: '#333', fontSize: '15px', fontWeight: '500' }}>
                  {getCurrencyInfoByCurrencyCode(sendMoneyOverview?.sendCurrency).symbol}{formatAmount(sendMoneyOverview?.sendAmount)}
                </span>
              </div>
            )}

            {/* Guaranteed rate */}
            {!localTransferType && (
              <div className="d-flex justify-content-between align-items-center pb-3">
                <span style={{ color: '#666', fontSize: '15px' }}>Guaranteed rate</span>
                <span style={{ color: '#333', fontSize: '15px', fontWeight: '500' }}>
                  {sendMoneyOverview?.exchangeRateAdmin ? (
                    sendMoneyOverview?.exchangeRateAdmin
                  ) : (
                    <>
                      1 {sendMoneyOverview?.sendCurrency} = {formatAmount(sendMoneyDetails?.exchangeRate)} {sendMoneyOverview?.receiveCurrency}
                    </>
                  )} 
                </span>
              </div>
            )}

            <hr style={{ borderTop: '1px dashed #2f2f2f', margin: '0' }} />

            {/* Paying with */}
            <div className="d-flex justify-content-between align-items-center py-3">
              <span style={{ color: '#666', fontSize: '15px' }}>Paying with</span>
              <div className="text-end">
                <span style={{ color: '#2b5043', fontSize: '15px', fontWeight: '600' }} className="d-flex align-items-center justify-content-end">
                  Your Wallet
                </span>
                <span style={{ color: '#888', fontSize: '13px', display: 'block', marginTop: '2px' }}>
                  Balance: {getCurrencyInfoByCurrencyCode(sendMoneyOverview?.sendCurrency).symbol}{formatAmount(sendMoneyOverview?.userWalletBalance)}
                </span>
              </div>
            </div>

            <hr style={{ borderTop: '1px dashed #2f2f2f', margin: '0' }} />
          </div>

          {/* Button */}
          <div className="d-flex align-items-center justify-content-center mt-5 mb-3 px-2 px-md-4">
            <button 
              className="btn primary__btn w-100 py-3"
              style={{ borderRadius: '30px', fontSize: '16px', fontWeight: '600' }}
              onClick={() => {
                if (!hasSetPin) {
                  toast.error("You need to set a transaction PIN before making transactions.");
                  const pinModal = new Modal(document.getElementById('transactionPinModal'));
                  pinModal.show();
                } else {
                  const payModal = new Modal(document.getElementById('payModal'));
                  payModal.show();
                }
              }}
            >
              Continue to payment
            </button>
          </div>

          {/* Remaining Balance */}
          <div className="text-center pb-2">
            <p className='text-muted' style={{ fontSize: '13px' }}>
              New Balance after payment: &nbsp;
              <span style={{ fontWeight: '500', color: '#555' }}>
                {getCurrencyInfoByCurrencyCode(sendMoneyOverview?.sendCurrency).symbol}
                {sendMoneyOverview.remainingWalletBalance 
                  ? sendMoneyOverview.remainingWalletBalance 
                  : calculateRemainingBalance(sendMoneyOverview?.userWalletBalance, sendMoneyOverview?.totalAmount)}
              </span>
            </p>
          </div>

          {/* New PIN Modal – replaces the old payModal */}
          <TransactionPinModal
            id="payModal"
            onSubmit={handleConfirmPayment}
            title="Confirm Money Transfer"
            description="Please enter your 4-digit PIN to complete this transaction."
          />

        </div>
      </div>
    </section>
  )
}

export default Step3SendMoney;